import type { ApiRequestOptions } from './swagger/core/ApiRequestOptions';
import type { ApiResult } from './swagger/core/ApiResult';
import { BaseHttpRequest } from './swagger/core/BaseHttpRequest';
import { CancelablePromise } from './swagger/core/CancelablePromise';
import type { OpenAPIConfig } from './swagger/core/OpenAPI';
import {
  catchErrorCodes,
  getFormData,
  getHeaders,
  getRequestBody,
  sendRequest,
} from './swagger/core/request';
import { SwaggerClient } from './swagger/SwaggerClient';
import { getToken } from '@/helpers/apiRequestHelper';

export const swaggerClient: SwaggerClient = new SwaggerClient({
  BASE: process.env.NEXT_PUBLIC_BACKEND_DOMAIN?.slice(0, -1),
  TOKEN: async () => {
    return await getToken();
  },
  HEADERS: {
    APP_VERSION: process.env.NEXT_PUBLIC_API_APP_VERSION || '',
    ReloadCount:
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('reload-count') || '0'
        : '0',
  },
});

class BlobDataFetchHttpRequest extends BaseHttpRequest {
  constructor(config: OpenAPIConfig) {
    super(config);
  }

  public override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    const url = `${this.config.BASE}${options.url}`;

    return new CancelablePromise(async (resolve, reject, onCancel) => {
      try {
        const formData = getFormData(options);
        const body = getRequestBody(options);
        const headers = await getHeaders(this.config, options);

        if (!onCancel.isCancelled) {
          const response = await sendRequest(
            this.config,
            options,
            url,
            body,
            formData,
            headers,
            onCancel,
          );

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const blob: any = await response.blob();

          const result: ApiResult = {
            url,
            ok: response.ok,
            status: response.status,
            statusText: response.statusText,
            body: blob,
          };

          catchErrorCodes(options, result);

          resolve(result.body);
        }
      } catch (error) {
        reject(error);
      }
    });
  }
}

const swaggerClientConfig = swaggerClient.request.config;
export const swaggerClientForBlobData: SwaggerClient = new SwaggerClient(
  swaggerClientConfig,
  BlobDataFetchHttpRequest,
);

/**
 * 短縮url取得API用のclient
 * ログインなしでも使用できるように専用の認証キーをAuthorizationヘッダーに渡しています
 **/
export const swaggerClientForShortUrl: SwaggerClient = new SwaggerClient({
  BASE: process.env.NEXT_PUBLIC_BACKEND_DOMAIN?.slice(0, -1),
  HEADERS: {
    APP_VERSION: process.env.NEXT_PUBLIC_API_APP_VERSION || '',
    Authorization: process.env.NEXT_PUBLIC_SHORT_URL_API_TOKEN || '',
    ReloadCount:
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('reload-count') || '0'
        : '0',
  },
});

/**
 * 非会員用のclient
 * ログインなしでも使用できるように専用の認証キーをAuthorizationヘッダーに渡しています
 **/
export const swaggerClientForNonMember: SwaggerClient = new SwaggerClient({
  BASE: process.env.NEXT_PUBLIC_BACKEND_DOMAIN?.slice(0, -1),
  HEADERS: {
    APP_VERSION: process.env.NEXT_PUBLIC_API_APP_VERSION || '',
    Authorization: process.env.NEXT_PUBLIC_NON_MEMBER_API_TOKEN || '',
    ReloadCount:
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem('reload-count') || '0'
        : '0',
  },
});
